import { FormikContextType, useFormikContext } from 'formik';
import React, { useMemo } from 'react';
import { Card, DateField, FormCell, Grid, NumberField, SelectField, TextField, ToggleSwitch } from 'ui-toolkit';
import { Show } from '../shared/Show';
import { TransactionCategorySelect } from './TransactionCategorySelect';
import { nbsp } from '../../utilities/administration/magicStrings';
import { NewOrderSpaceWrapping } from './wrappers/NewOrderSpaceWrapper';
import { DisplaySplitClosing } from '../../ui-toolkit/enums/eSplitClosing';
import { optionsFromDisplay } from '../../ui-toolkit/enums/optionsFromDisplay';
import { eTransactionCategory } from 'ui-toolkit/enums/eTransactionType';
import { eAccessControlUserType } from '../../ui-toolkit/enums/eAccessControlUserType';
import { DisplayPropertyType } from '../../ui-toolkit/orders/interfaces/ePropertyType';
import { DisplayOccupancyType } from '../../ui-toolkit/orders/interfaces/eOccupancyType';
import { DisplayLoanType } from '../../ui-toolkit/orders/interfaces/eLoanType';
import { ICreateOrderRequest } from '../../ui-toolkit/orders/interfaces/createOrder/ICreateOrderRequest';

export const clearGroupValues = (formik: FormikContextType<ICreateOrderRequest>) => {
  const formValues = formik.values;
  if ('mobility' in formValues) {
    delete formValues.mobility;
  }
  if ('mobilityGroup' in formValues) {
    delete formValues.mobilityGroup;
  }
  formik.setFieldValue('relocationEmployerName', undefined);
  formik.setFieldValue('groupId', undefined);
};

export const TransactionInformationForm: React.FC<{
  autoFocus: boolean;
  isGroup: boolean;
  setIsGroup: React.Dispatch<React.SetStateAction<boolean>>;
  userType: eAccessControlUserType;
  canSelectClient: boolean;
}> = ({ autoFocus, isGroup, setIsGroup, userType, canSelectClient }) => {
  const propertyTypeOptions = useMemo(() => {
    return optionsFromDisplay(DisplayPropertyType);
  }, []);
  const occupancyTypeOptions = useMemo(() => optionsFromDisplay(DisplayOccupancyType), []);
  const loanTypeOptions = useMemo(() => optionsFromDisplay(DisplayLoanType), []);
  const splitClosingOptions = useMemo(() => optionsFromDisplay(DisplaySplitClosing), []);

  const formik = useFormikContext<ICreateOrderRequest>();
  const { transactionCategory } = formik.values;
  return (
    <NewOrderSpaceWrapping>
      <Card title={`Transaction Information`}>
        <Grid>
          <Grid columnCount={5}>
            <TransactionCategorySelect autoFocus={autoFocus} />
            <Show when={canSelectClient}>
              <SelectField
                name="splitClosing"
                label={'Split Closing'}
                options={splitClosingOptions}
                // isMenuPlacementTop
                isRequired={true}
              />
            </Show>
            <SelectField
              name="propertyType"
              data-test-id={'property_type'}
              options={propertyTypeOptions}
              label={`Property Type`}
              isClearable
              placeholder={`Select a category`}
            />
            <SelectField
              name="occupancyType"
              data-test-id={`occupancy_type`}
              options={occupancyTypeOptions}
              label={`Occupancy Type`}
              isClearable
              placeholder={`Select a category`}
            />
          </Grid>
          <Grid columnCount={5}>
            <TextField
              name="externalPartyTransactionId"
              data-test-id={`external_party_transaction_id`}
              label={`Transaction ID`}
              placeholder={'##########'}
            />
            <TextField name={`loanId`} data-test-id={`loan_id`} label={`Loan Number`} placeholder={'##########'} />
            <SelectField
              name="loanType"
              data-test-id={`loan_type`}
              options={loanTypeOptions}
              label={`Loan Type`}
              isClearable
              placeholder={`Select a category`}
            />
            <NumberField
              name="loanAmount"
              data-test-id={`loan_amount`}
              label={`Loan Amount`}
              placeholder={`$ ###,###,###.##`}
              allowNegative={false}
              mode={'currency'}
              isRequired={transactionCategory !== eTransactionCategory.Purchase}
            />
            <Show when={transactionCategory === eTransactionCategory.Purchase} fallback={<div />}>
              <NumberField
                name="purchasePrice"
                data-test-id={`purchase_price`}
                label={`Purchase Price`}
                isRequired={true}
                placeholder={`$ ###,###,###.##`}
                allowNegative={false}
                mode={'currency'}
              />
            </Show>
          </Grid>
          <Grid columnCount={4}>
            <DateField
              name="rateLockExpiration"
              data-test-id={`rate_lock_expiration`}
              label={`Rate Lock Expiration`}
              placeholder={`MM/DD/YYYY`}
              highlightDate={'today'}
              preventPastDates={true}
            />
            <Show when={transactionCategory === eTransactionCategory.Purchase}>
              <DateField
                name="closingDate"
                label={`Closing Date`}
                isRequired={false}
                placeholder={`MM/DD/YYYY`}
                data-test-id={`closing_date`}
                preventPastDates={true}
              />
            </Show>
            <Show when={transactionCategory === eTransactionCategory.Refinance}>
              <NumberField
                name="estimatedPropertyValue"
                data-test-id={`estimated_property_value`}
                label={`Estimated Property Value`}
                placeholder={`$ ###,###,###.##`}
                allowNegative={false}
                mode={'currency'}
              />
            </Show>
            <Show when={transactionCategory === eTransactionCategory.Refinance}>
              <DateField
                name="estimatedClosingDate"
                label={`Estimated Closing Date`}
                placeholder={`MM/DD/YYYY`}
                data-test-id={`estimated_closing_date`}
                preventPastDates={true}
              />
            </Show>
            <Show when={userType === eAccessControlUserType.Admin}>
              <FormCell label={nbsp}>
                <ToggleSwitch
                  checked={isGroup}
                  id={'toggle-is-group'}
                  onChange={() => {
                    if (isGroup) {
                      clearGroupValues(formik);
                    }
                    setIsGroup((prevState) => !prevState);
                  }}
                  value={''}
                  label={'Create Order Group'}
                />
              </FormCell>
            </Show>
          </Grid>
        </Grid>
      </Card>
    </NewOrderSpaceWrapping>
  );
};
